import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { apiService, type SessionRenewal } from '../services/ApiService';
import { keepUserPreferencesLocal, reloadUserPreferences, resetUserPreferences } from '../hooks/useUserPreference';
import { trackEvent } from '../services/usageMetrics';
import { logger } from '../utils/log';

export interface User {
  id: string;
  email: string;
  name: string;
  organization?: string;
  /** Account creation time (ISO) — e.g. release notes skip accounts newer than the release. */
  createdAt?: string;
  // Note: Role/permission info comes from RBACContext, not here
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Use ref to store token for synchronous access
  const accessTokenRef = useRef<string | null>(null);
  // The renewal timer, and when the session key runs out (audit bug 81). A ref, not state: the
  // timer used to live in state that the functions read from an old render, so a new timer never
  // cleared the previous one.
  const renewalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiresAtRef = useRef<number>(0);

  // Auto-refresh token 5 minutes before expiry
  const scheduleTokenRefresh = (expiresInMs: number) => {
    if (renewalTimerRef.current) clearTimeout(renewalTimerRef.current);
    expiresAtRef.current = Date.now() + expiresInMs;

    // Schedule refresh 5 minutes before expiry (or halfway through if less than 10 min)
    const refreshTime = expiresInMs > 10 * 60 * 1000
      ? expiresInMs - 5 * 60 * 1000  // 5 min before expiry
      : expiresInMs / 2;              // Halfway through

    renewalTimerRef.current = setTimeout(() => {
      refreshToken();
    }, refreshTime);
  };

  // Login function
  const login = async (email: string, password: string) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include', // Important: Include cookies
      });

      if (!response.ok) {
        // Rate-limited: show a human message (the limiter now sends JSON, but
        // stay tolerant of any non-JSON body — proxies can produce text too).
        if (response.status === 429) {
          const he = (() => { try { return localStorage.getItem('mabat3d-language') === 'he'; } catch { return false; } })();
          throw new Error(he
            ? 'יותר מדי ניסיונות התחברות — נסו שוב בעוד מספר דקות'
            : 'Too many login attempts — try again in a few minutes');
        }
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();

      logger.debug('✅ Login successful! Setting accessToken:', data.accessToken ? `${data.accessToken.substring(0, 20)}...` : 'null');

      // Store access token in ref immediately (synchronous!)
      accessTokenRef.current = data.accessToken;
      logger.debug('🔒 Stored token in ref synchronously');

      // Also store in state for React rendering
      setAccessToken(data.accessToken);
      setUser(data.user);
      setIsAuthenticated(true);
      apiService.setAuthReady();
      reloadUserPreferences();
      trackEvent('app.login');

      logger.debug('📝 Login: Set accessToken, user, and isAuthenticated=true');

      // Schedule token refresh
      scheduleTokenRefresh(data.expiresIn);

    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    // Whether someone was actually logged in. logout() also runs on every visit WITHOUT a
    // session (the start-up refresh fails and lands here), so the reload below must only fire
    // for a real session — otherwise a logged-out visitor would reload forever.
    const hadSession = !!accessTokenRef.current;
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state regardless of API call success
      accessTokenRef.current = null;
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
      apiService.resetAuthReady();
      resetUserPreferences();

      if (renewalTimerRef.current) {
        clearTimeout(renewalTimerRef.current);
        renewalTimerRef.current = null;
      }
      expiresAtRef.current = 0;

      // A LOGOUT STARTS CLEAN. The app shows the login screen but used to keep everything it
      // held for the last session: the viewer blocker's "3D is ready" flag (so after logging
      // back in the models reloaded with no blocker — Idan, 2026-09-25), the previous person's
      // site, filters and open panels, services' caches and the old 3D viewer. Reloading the
      // page drops all of it; the server session is already gone, so the reload lands on login.
      if (hadSession) window.location.reload();
    }
  };

  /**
   * ONE renewal (audit bug 81), for everything that needs it: the timer, a request answered 401,
   * an upload, waking up. Only a refusal (401/403) signs out; a server that cannot be reached, or
   * answers with an error of its own, leaves the person signed in and is tried again.
   */
  const renewSessionOnce = async (): Promise<SessionRenewal> => {
    let response: Response;
    try {
      logger.debug('[AuthContext] Renewing the session...');
      response = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
    } catch (error) {
      logger.debug('[AuthContext] Renewal could not reach the server:', error);
      return { unreachable: true };
    }
    if (response.status === 401 || response.status === 403) {
      logger.debug('[AuthContext] Renewal refused, logging out');
      await logout();
      return { refused: true };
    }
    if (!response.ok) return { unreachable: true };
    try {
      const data = await response.json();
      logger.debug('[AuthContext] Refresh successful, user:', data.user?.email);
      // Update access token in ref immediately (synchronous!)
      accessTokenRef.current = data.accessToken;
      setAccessToken(data.accessToken);
      setUser(data.user);
      setIsAuthenticated(true);
      apiService.setAuthReady();
      reloadUserPreferences();
      scheduleTokenRefresh(data.expiresIn);
      return { token: data.accessToken };
    } catch {
      return { unreachable: true };
    }
  };

  // Refresh token function: the shared renewal. Not reached: tried again in 30 s while signed in;
  // with no session yet (start-up), the sign-in screen as before.
  const refreshToken = async () => {
    const renewal = await apiService.renewSession();
    if (!('unreachable' in renewal)) return;
    if (!accessTokenRef.current) {
      await logout();
      return;
    }
    if (renewalTimerRef.current) clearTimeout(renewalTimerRef.current);
    renewalTimerRef.current = setTimeout(() => { refreshToken(); }, 30 * 1000);
  };

  // Try to refresh token on mount (restore session)
  useEffect(() => {
    // Set up ApiService integration - token getter reads from ref for synchronous access
    apiService.setTokenGetter(() => accessTokenRef.current);
    apiService.setUnauthorizedCallback(() => logout());
    apiService.setSessionRenewer(renewSessionOnce);

    // Waking up (a laptop opened, a tab shown again, the network back): renew first when the key
    // has run out or is about to, so the first request is not sent with an expired key.
    const renewIfStale = () => {
      if (document.visibilityState !== 'visible' || !accessTokenRef.current) return;
      if (Date.now() > expiresAtRef.current - 60 * 1000) void refreshToken();
    };
    document.addEventListener('visibilitychange', renewIfStale);
    window.addEventListener('online', renewIfStale);
    window.addEventListener('focus', renewIfStale);

    const initAuth = async () => {
      try {
        await refreshToken();
      } catch (error) {
        console.error('Failed to restore session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      if (renewalTimerRef.current) clearTimeout(renewalTimerRef.current);
      document.removeEventListener('visibilitychange', renewIfStale);
      window.removeEventListener('online', renewIfStale);
      window.removeEventListener('focus', renewIfStale);
      apiService.setSessionRenewer(null);
    };
    // Boot-time session restore: must fire exactly once. logout/refreshToken are
    // re-created every render, so depending on them would re-POST /api/auth/refresh
    // on every state change this provider makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Datadog sessions carry the logged-in person (login, restored session, logout). Loaded
  // lazily like the RUM init itself; the embed shell has its own provider and stays untracked.
  useEffect(() => {
    import('../services/datadogInit')
      .then(({ setDatadogUser }) => setDatadogUser(user))
      .catch(() => { /* monitoring must never break the app */ });
  }, [user]);

  // Get access token for API calls (exported for ApiService)
  const getAccessToken = () => accessToken;

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshToken,
    getAccessToken: getAccessToken as any, // Make available to consumers
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * EmbedAuthProvider — auth context for the external /embed shell.
 *
 * Serves a STATIC partner embed token instead of the login/refresh flow:
 * no cookies, no /api/auth calls, no logout-on-401 (an expired embed token is
 * handled by the embed bridge's tokenExpiring/setToken renewal, not by
 * bouncing to a login screen). Token updates arrive via the `token` prop when
 * the parent page sends a fresh one over postMessage.
 */
export const EmbedAuthProvider: React.FC<{ token: string; children: ReactNode }> = ({ token, children }) => {
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Wire ApiService during the provider's FIRST RENDER, not in an effect:
  // React runs child effects before parent effects, so an effect here would
  // fire AFTER the viewer shell's data-loading effect — its first API calls
  // would go out with no Authorization header and 401 (caught live 2026-07-19).
  useState(() => {
    apiService.setTokenGetter(() => tokenRef.current);
    apiService.setUnauthorizedCallback(() => {
      logger.debug('[EmbedAuth] 401 from API — waiting for setToken renewal from parent');
    });
    // Embed tokens are static — auth is ready the moment the shell exists.
    apiService.setAuthReady();
    // No user behind an embed token: preferences stay in memory (no GET/PATCH /users/me/preferences).
    keepUserPreferencesLocal();
    return true;
  });

  const value: AuthContextType = {
    user: { id: 'embed', email: 'embed@mabat3d', name: 'Embed session' },
    isAuthenticated: true,
    isLoading: false,
    login: async () => { throw new Error('login is not available in embed mode'); },
    logout: async () => { /* no session to end in embed mode */ },
    refreshToken: async () => { /* renewal comes from the parent via setToken */ },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Export the context for direct access to getAccessToken if needed
export { AuthContext };
